import { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  Platform,
  Pressable,
  ActivityIndicator,
} from 'react-native';
import { router } from 'expo-router';
import { Plus, ExternalLink, Copy, Settings, ChevronDown, ChevronUp } from 'lucide-react-native';
import { supabase } from '@/utils/supabase';
import { theme } from '@/constants/theme';
import { trackKursiPlayEvent } from '@/utils/kursiPlayTelemetry';
import {
  ensureKursiPlayOpenAccessSession,
  STATUS_CONFIG,
  LANGUAGE_LABELS,
  type CourseStatus,
} from '@/utils/kursiPlay';
import { publishAiCourse } from '@/utils/kursiPlayCreatorService';
import { MagicBento, ProfileCard } from '@/components/learn-premium/PremiumPrimitives';

const BG       = '#0B0B14';
const SURFACE  = '#16142A';
const SURFACE2 = '#221F3D';
const SURFACE3 = '#2D2B50';
const TEXT     = '#F0EFF8';
const TEXT_MED = '#9896B4';
const TEXT_DIM = '#6B6B85';
const PRIMARY  = theme.colors.primary;
const GREEN    = '#10B981';
const AMBER    = '#F59E0B';
const RED      = '#EF4444';

type World = {
  id: string;
  slug: string;
  world_name: string;
  description: string | null;
  status: CourseStatus;
  language: string;
  created_at: string;
  published_at: string | null;
  schema_version: string | null;
};

type LessonDropoff = {
  lessonId: string;
  title: string;
  orderIndex: number;
  started: number;
  completed: number;
  dropoffRate: number;
};

type WorldAnalytics = {
  totalLessons: number;
  completedUsers: number;
  shareOpens: number;
  avgXp: number;
  needsAttentionLessons: number;
  completionRate: number;
  lessonDropoff: LessonDropoff[];
};

function getManagementRoute(world: World) {
  return `/learn/creator/manage/${world.id}`;
}

function getNextBestCreatorAction(world: World): string {
  if (world.status === 'draft') return 'הצעד הבא: השלם שער איכות ופרסם';
  if (world.status === 'published') return 'הצעד הבא: בדוק אנליטיקה ושפר שיעור עם נשירה גבוהה';
  return 'הצעד הבא: בדוק תוכן ועדכן לפני פרסום מחדש';
}

export default function CreatorDashboard() {
  const [worlds, setWorlds] = useState<World[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedSlug, setCopiedSlug] = useState<string | null>(null);
  const [publishingId, setPublishingId] = useState<string | null>(null);
  const [publishError, setPublishError] = useState<string | null>(null);

  const handleCreateNew = useCallback(() => {
    router.push('/learn/path/create' as any);
  }, []);

  const loadWorlds = useCallback(async () => {
    setLoading(true);
    setError(null);
    setPublishError(null);
    await ensureKursiPlayOpenAccessSession(supabase);

    const { data, error: err } = await supabase
      .from('kp_worlds')
      .select('id, slug, world_name, description, status, language, created_at, published_at, schema_version')
      .order('created_at', { ascending: false });

    if (err) {
      trackKursiPlayEvent('kursi_play_error', {
        location: 'creator_dashboard_load_worlds',
        message: err.message,
      });
      setError('שגיאה בטעינת המסלולים: ' + err.message);
    } else {
      setWorlds((data as World[]) || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    trackKursiPlayEvent('creator_dashboard_opened');
    loadWorlds();
  }, [loadWorlds]);

  const handleCopy = useCallback(async (slug: string) => {
    if (Platform.OS !== 'web') return;
    const url = `${window.location.origin}/learn/path/${slug}`;
    try { await navigator.clipboard.writeText(url); } catch { /* ignore */ }
    trackKursiPlayEvent('share_link_copied', { slug, source: 'creator_dashboard' });
    setCopiedSlug(slug);
    setTimeout(() => setCopiedSlug((s) => (s === slug ? null : s)), 2000);
  }, []);

  const handlePublishWorld = useCallback(async (worldId: string) => {
    setPublishingId(worldId);
    setPublishError(null);
    try {
      await publishAiCourse(worldId);
      setWorlds((prev) =>
        prev.map((w) => (w.id === worldId ? { ...w, status: 'published' as CourseStatus, published_at: new Date().toISOString() } : w)),
      );
    } catch (err: any) {
      const message = err?.message ?? 'הפרסום נכשל. אפשר לנסות שוב בעוד רגע.';
      setPublishError(message);
      trackKursiPlayEvent('kursi_play_error', {
        location: 'creator_dashboard_publish_world',
        worldId,
        message,
      });
    } finally {
      setPublishingId(null);
    }
  }, []);

  if (loading) {
    return (
      <View style={s.loadingRoot}>
        <ActivityIndicator color={PRIMARY} size="large" />
        <Text style={s.loadingText}>טוען מסלולים...</Text>
      </View>
    );
  }

  return (
    <ScrollView style={s.bg} contentContainerStyle={s.container} showsVerticalScrollIndicator={false}>

      <View style={s.header}>
        <Pressable style={s.createBtn} onPress={handleCreateNew} accessibilityLabel="צור מסלול חדש" accessibilityRole="button">
          <Plus color="#fff" size={15} strokeWidth={2.5} />
          <Text style={s.createBtnText}>מסלול חדש</Text>
        </Pressable>
        <View style={{ flex: 1 }}>
          <Text style={s.headerTitle}>המסלולים שלי</Text>
          <Text style={s.headerSub}>
            {worlds.length === 0 ? 'עדיין לא נוצרו מסלולים' : `${worlds.length} מסלולים`}
          </Text>
        </View>
      </View>
      <ProfileCard
        name="Kursi Creator"
        role="זהות יוצר"
        meta={`${worlds.length} מסלולים · סטודיו KURSI LEARN`}
      />
      <MagicBento
        blocks={[
          { icon: '🚀', title: 'פרסום מהיר', body: 'מעביר מסלול מטיוטה לפרסום עם בדיקות תקינות מובנות.' },
          { icon: '📉', title: 'נשירה לפי שיעור', body: 'זיהוי נקודות נטישה והבנה היכן לשפר את החוויה.' },
          { icon: '🔗', title: 'שיתוף וניטור', body: 'קישור פתוח למסלול עם מדדים וקצב השלמה.' },
          { icon: '🛠️', title: 'עריכה עמוקה', body: 'שליטה במטא, שיעורים, תרחישים והגדרות מסלול.' },
        ]}
      />

      {error && (
        <View style={s.errorBox} accessibilityRole="alert">
          <Text style={s.errorText}>{error}</Text>
          <Pressable onPress={() => loadWorlds()} accessibilityRole="button">
            <Text style={s.retryText}>נסה שוב</Text>
          </Pressable>
        </View>
      )}

      {!error && worlds.length === 0 && <EmptyState onCreatePress={handleCreateNew} />}

      {publishError && (
        <View style={s.errorBox} accessibilityRole="alert">
          <Text style={s.errorText}>{publishError}</Text>
          <Pressable onPress={() => setPublishError(null)} accessibilityRole="button">
            <Text style={s.retryText}>סגור</Text>
          </Pressable>
        </View>
      )}

      {worlds.map((world) => {
        const sc = STATUS_CONFIG[world.status] ?? STATUS_CONFIG.draft;
        const isCopied = copiedSlug === world.slug;
        const isPublished = world.status === 'published';

        return (
          <View key={world.id} style={s.courseCard}>
            <View style={[s.courseCardAccent, { backgroundColor: isPublished ? GREEN : PRIMARY }]} />
            <View style={s.courseCardBody}>

              <View style={s.cardTopRow}>
                <View style={s.cardBadges}>
                  <View style={[s.statusBadge, { backgroundColor: sc.bg + 'CC', borderColor: sc.color + '44' }]}>
                    <Text style={[s.statusText, { color: sc.color }]}>{sc.label}</Text>
                  </View>
                  <View style={s.v2Badge}>
                    <Text style={s.v2BadgeText}>למידה אינטראקטיבית</Text>
                  </View>
                </View>
              </View>

              <Text style={s.courseName} numberOfLines={2}>{world.world_name}</Text>
              {world.description ? (
                <Text style={s.courseDesc} numberOfLines={2}>{world.description}</Text>
              ) : null}

              <View style={s.statsRow}>
                <StatPill
                  icon="🌐"
                  value={LANGUAGE_LABELS[world.language as keyof typeof LANGUAGE_LABELS] ?? world.language}
                />
                <StatPill
                  icon="📅"
                  value={new Date(world.created_at).toLocaleDateString('he-IL')}
                />
                {world.published_at && (
                  <StatPill
                    icon="🚀"
                    value={new Date(world.published_at).toLocaleDateString('he-IL')}
                  />
                )}
              </View>

              {isPublished && <AnalyticsPanel worldId={world.id} worldSlug={world.slug} />}

              <View style={s.nextActionRow}><Text style={s.nextActionText}>{getNextBestCreatorAction(world)}</Text></View>

              <View style={s.cardActions}>
                <Pressable
                  style={s.actionBtn}
                  onPress={() => router.push(getManagementRoute(world) as any)}
                  accessibilityLabel={`ניהול מסלול ${world.world_name}`}
                  accessibilityRole="button"
                >
                  <Settings color={PRIMARY} size={14} strokeWidth={2} />
                  <Text style={[s.actionBtnText, { color: PRIMARY }]}>ניהול</Text>
                </Pressable>

                {isPublished && (
                  <Pressable
                    style={s.actionBtn}
                    onPress={() => router.push(`/learn/path/${world.slug}` as any)}
                    accessibilityRole="link"
                  >
                    <ExternalLink color={TEXT_MED} size={14} strokeWidth={2} />
                    <Text style={s.actionBtnText}>תצוגה</Text>
                  </Pressable>
                )}

                {isPublished && (
                  <Pressable
                    style={[s.actionBtn, isCopied && s.actionBtnCopied]}
                    onPress={() => handleCopy(world.slug)}
                    accessibilityLabel={isCopied ? 'קישור הועתק' : `העתק קישור למסלול ${world.world_name}`}
                    accessibilityRole="button"
                  >
                    <Copy color={isCopied ? GREEN : TEXT_MED} size={14} strokeWidth={2} />
                    <Text style={[s.actionBtnText, { color: isCopied ? GREEN : TEXT_MED }]}>
                      {isCopied ? 'הועתק ✓' : 'קישור'}
                    </Text>
                  </Pressable>
                )}

                {world.status === 'draft' && (
                  <Pressable
                    style={s.publishBtn}
                    onPress={() => handlePublishWorld(world.id)}
                    disabled={publishingId === world.id}
                    accessibilityRole="button"
                  >
                    {publishingId === world.id
                      ? <ActivityIndicator size="small" color={GREEN} />
                      : <Text style={s.publishBtnText}>פרסם עכשיו →</Text>}
                  </Pressable>
                )}
              </View>
            </View>
          </View>
        );
      })}

      {worlds.length > 0 && (
        <Pressable style={s.createMoreBtn} onPress={handleCreateNew} accessibilityLabel="צור מסלול נוסף" accessibilityRole="button">
          <Plus color={PRIMARY} size={15} strokeWidth={2.5} />
          <Text style={s.createMoreText}>צור מסלול נוסף</Text>
        </Pressable>
      )}

      <View style={{ height: 48 }} />
    </ScrollView>
  );
}

function AnalyticsPanel({ worldId, worldSlug }: { worldId: string; worldSlug: string }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<WorldAnalytics | null>(null);

  const load = useCallback(async () => {
    if (data) return;
    setLoading(true);

    const [{ data: lessonRows }, { data: progressRows }, shareCopiesResult, xpResult] = await Promise.all([
      supabase.from('kp_lessons').select('id, title, order_index').eq('world_id', worldId).order('order_index'),
      supabase.from('kp_user_lesson_progress').select('user_id, lesson_id, completed').eq('world_id', worldId),
      supabase.from('kp_telemetry_events_canonical').select('id', { count: 'exact', head: true })
        .eq('canonical_event', 'learn_share_opened').contains('payload', { slug: worldSlug }),
      supabase.from('kp_user_world_progress').select('total_xp_earned').eq('world_id', worldId),
    ]);

    const lessons = lessonRows ?? [];
    const progress = progressRows ?? [];

    const uniqueCompletedUsers = new Set(
      progress.filter((p: any) => p.completed).map((p: any) => p.user_id)
    ).size;

    const xpValues = (xpResult.data ?? []).map((r: any) => r.total_xp_earned ?? 0);
    const avgXp = xpValues.length > 0 ? Math.round(xpValues.reduce((a: number, b: number) => a + b, 0) / xpValues.length) : 0;

    const lessonDropoff: LessonDropoff[] = lessons.map((l: any) => {
      const lessonProgress = progress.filter((p: any) => p.lesson_id === l.id);
      const started = lessonProgress.length;
      const completed = lessonProgress.filter((p: any) => p.completed).length;
      const dropoffRate = started > 0 ? Math.round(((started - completed) / started) * 100) : 0;
      return { lessonId: l.id, title: l.title, orderIndex: l.order_index, started, completed, dropoffRate };
    });

    const needsAttentionLessons = lessonDropoff.filter((ld) => ld.dropoffRate >= 40).length;
    const completionRate = lessons.length > 0
      ? Math.round((lessonDropoff.filter((ld) => ld.started > 0 && ld.dropoffRate <= 25).length / lessons.length) * 100)
      : 0;

    setData({
      totalLessons: lessons.length,
      completedUsers: uniqueCompletedUsers,
      shareOpens: shareCopiesResult.count ?? 0,
      avgXp,
      needsAttentionLessons,
      completionRate,
      lessonDropoff,
    });
    setLoading(false);
  }, [worldId, worldSlug, data]);

  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    if (next) load();
  };

  return (
    <View style={ap.container}>
      <Pressable style={ap.toggle} onPress={handleToggle} accessibilityRole="button">
        {open
          ? <ChevronUp color={TEXT_DIM} size={14} strokeWidth={2} />
          : <ChevronDown color={TEXT_DIM} size={14} strokeWidth={2} />}
        <Text style={ap.toggleLabel}>אנליטיקה (חלון: 30 הימים האחרונים)</Text>
      </Pressable>

      {open && (
        <View style={ap.body}>
          {loading && <ActivityIndicator color={PRIMARY} size="small" style={{ marginVertical: 14 }} />}

          {!loading && data && (
            <>
              <View style={ap.statsRow}>
                <StatBox icon="📚" value={String(data.totalLessons)} label="שיעורים" color={PRIMARY} />
                <StatBox icon="✅" value={`${data.completionRate}%`} label="שיעורים יציבים" color={GREEN} />
                <StatBox icon="⚠️" value={String(data.needsAttentionLessons)} label="דורשים טיפול" color={AMBER} />
                <StatBox icon="👥" value={String(data.completedUsers)} label="לומדים שסיימו" color={PRIMARY} />
              </View>
              <Text style={ap.insightText}>הגדרות מהירות: שיעורים יציבים = נשירה עד 25%. דורשים טיפול = נשירה של 40% ומעלה.</Text>
              <Text style={ap.insightText}>XP ממוצע למסלול: {data.avgXp > 0 ? data.avgXp : 'עדיין אין מספיק נתונים'} · פתיחות קישור: {data.shareOpens}</Text>

              {data.lessonDropoff.length > 0 && (
                <>
                  <Text style={ap.dropoffTitle}>נשירה לפי שיעור</Text>
                  {data.lessonDropoff.map((ld) => {
                    const barColor = ld.dropoffRate > 50 ? RED : ld.dropoffRate > 25 ? AMBER : GREEN;
                    const pct = Math.min(100, ld.dropoffRate);
                    return (
                      <View key={ld.lessonId} style={ap.lessonRow}>
                        <View style={ap.lessonRowTop}>
                          <Text style={[ap.dropoffPct, { color: barColor }]}>{ld.dropoffRate}%</Text>
                          <Text style={ap.lessonTitle} numberOfLines={1}>{ld.orderIndex + 1}. {ld.title}</Text>
                        </View>
                        <View style={ap.barTrack}>
                          <View style={[ap.barFill, { width: `${pct}%` as any, backgroundColor: barColor }]} />
                        </View>
                        <Text style={ap.lessonStats}>{ld.completed}/{ld.started} השלימו</Text>
                      </View>
                    );
                  })}
                </>
              )}
            </>
          )}
        </View>
      )}
    </View>
  );
}

function StatBox({ icon, value, label, color }: { icon: string; value: string; label: string; color: string }) {
  return (
    <View style={[ap.statBox, { borderColor: color + '28' }]}>
      <Text style={ap.statBoxIcon}>{icon}</Text>
      <Text style={[ap.statBoxValue, { color }]}>{value}</Text>
      <Text style={ap.statBoxLabel}>{label}</Text>
    </View>
  );
}

const ap = StyleSheet.create({
  container: {
    borderTopWidth: 1,
    borderTopColor: SURFACE3,
    marginTop: 8,
    paddingTop: 2,
  },
  toggle: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 10,
    alignSelf: 'flex-start',
  },
  toggleLabel: {
    fontFamily: theme.fonts.medium,
    fontSize: 13,
    color: TEXT_DIM,
  },
  body: { paddingBottom: 6, gap: 10 },
  statsRow: { flexDirection: 'row-reverse', gap: 8 },
  insightText: {
    fontFamily: theme.fonts.regular,
    fontSize: 11,
    color: TEXT_MED,
    textAlign: 'right',
    lineHeight: 17,
  },
  statBox: {
    flex: 1,
    backgroundColor: SURFACE2,
    borderRadius: 10,
    padding: 10,
    alignItems: 'center',
    gap: 3,
    borderWidth: 1,
  },
  statBoxIcon: { fontSize: 14 },
  statBoxValue: { fontFamily: theme.fonts.bold, fontSize: 16 },
  statBoxLabel: { fontFamily: theme.fonts.regular, fontSize: 10, color: TEXT_DIM, textAlign: 'center' },
  dropoffTitle: {
    fontFamily: theme.fonts.semiBold,
    fontSize: 12,
    color: TEXT_MED,
    textAlign: 'right',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    marginTop: 4,
  },
  lessonRow: { gap: 4 },
  lessonRowTop: { flexDirection: 'row-reverse', alignItems: 'center', gap: 6 },
  lessonTitle: { flex: 1, fontFamily: theme.fonts.regular, fontSize: 12, color: TEXT_MED, textAlign: 'right' },
  dropoffPct: { fontFamily: theme.fonts.bold, fontSize: 12, minWidth: 32, textAlign: 'right' },
  barTrack: { height: 4, borderRadius: 2, backgroundColor: SURFACE3, overflow: 'hidden' },
  barFill: { height: 4, borderRadius: 2 },
  lessonStats: { fontFamily: theme.fonts.regular, fontSize: 11, color: TEXT_DIM, textAlign: 'right' },
});

function EmptyState({ onCreatePress }: { onCreatePress: () => void }) {
  const WAYS = [
    { icon: '📄', label: 'מתוכן כתוב', sub: 'PDF / Word / TXT' },
    { icon: '🎮', label: 'מאפס', sub: 'AI יוצר הכל' },
    { icon: '🎥', label: 'מסלול וידאו', sub: 'תמלול / הרצאה' },
    { icon: '📦', label: 'ייבוא מסודר', sub: 'שמור על המבנה' },
  ];
  return (
    <View style={s.emptyState}>
      <View style={s.emptyIconWrap}>
        <Text style={s.emptyIcon}>🎓</Text>
      </View>
      <Text style={s.emptyTitle}>בנה את המסלול הראשון שלך</Text>
      <Text style={s.emptySub}>בחר שיטה — AI יבנה מסלול מלא תוך דקות</Text>
      <View style={s.emptyWaysGrid}>
        {WAYS.map((w) => (
          <View key={w.label} style={s.emptyWayCard}>
            <Text style={s.emptyWayIcon}>{w.icon}</Text>
            <Text style={s.emptyWayLabel}>{w.label}</Text>
            <Text style={s.emptyWaySub}>{w.sub}</Text>
          </View>
        ))}
      </View>
      <Pressable style={s.emptyBtn} onPress={onCreatePress} accessibilityLabel="צור מסלול עכשיו" accessibilityRole="button">
        <Text style={s.emptyBtnText}>צור מסלול עכשיו →</Text>
      </Pressable>
    </View>
  );
}

function StatPill({ icon, value }: { icon: string; value: string }) {
  return (
    <View style={s.statPill}>
      <Text style={s.statPillIcon}>{icon}</Text>
      <Text style={s.statPillText}>{value}</Text>
    </View>
  );
}

const s = StyleSheet.create({
  bg: { flex: 1, backgroundColor: BG },
  container: {
    paddingTop: Platform.OS === 'web' ? 40 : 60,
    paddingBottom: 40,
    paddingHorizontal: 20,
    maxWidth: 720,
    alignSelf: 'center' as any,
    width: '100%',
    gap: 14,
  },
  loadingRoot: {
    flex: 1,
    backgroundColor: BG,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
  },
  loadingText: { fontFamily: theme.fonts.regular, color: TEXT_MED, fontSize: 14 },

  header: {
    flexDirection: 'row-reverse',
    alignItems: 'flex-start',
    gap: 12,
    paddingBottom: 6,
  },
  headerTitle: { fontFamily: theme.fonts.bold, fontSize: 22, color: TEXT, textAlign: 'right' },
  headerSub: { fontFamily: theme.fonts.regular, fontSize: 13, color: TEXT_MED, textAlign: 'right', marginTop: 3 },

  createBtn: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 6,
    backgroundColor: PRIMARY,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 10,
    ...(Platform.OS === 'web' ? ({ boxShadow: `0 4px 14px ${PRIMARY}55` } as any) : {}),
  },
  createBtnText: { fontFamily: theme.fonts.bold, fontSize: 13, color: '#fff' },

  errorBox: {
    backgroundColor: RED + '0F',
    borderRadius: 12,
    padding: 14,
    borderWidth: 1,
    borderColor: RED + '33',
    gap: 6,
    alignItems: 'flex-end',
  },
  errorText: { fontFamily: theme.fonts.regular, fontSize: 14, color: RED, textAlign: 'right' },
  retryText: { fontFamily: theme.fonts.semiBold, fontSize: 13, color: PRIMARY },

  courseCard: {
    backgroundColor: SURFACE,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: SURFACE3,
    overflow: 'hidden',
    flexDirection: 'row-reverse',
    ...(Platform.OS === 'web' ? ({ boxShadow: '0 4px 20px rgba(0,0,0,0.35)' } as any) : {}),
  },
  courseCardAccent: { width: 3, flexShrink: 0 },
  courseCardBody: {
    flex: 1,
    padding: 16,
    gap: 10,
  },

  cardTopRow: { flexDirection: 'row-reverse', alignItems: 'center', justifyContent: 'space-between' },
  cardBadges: { flexDirection: 'row-reverse', gap: 7, flexWrap: 'wrap' },
  statusBadge: {
    paddingHorizontal: 10,
    paddingVertical: 3,
    borderRadius: 20,
    borderWidth: 1,
  },
  statusText: { fontFamily: theme.fonts.semiBold, fontSize: 11 },
  v2Badge: {
    paddingHorizontal: 10,
    paddingVertical: 3,
    borderRadius: 20,
    backgroundColor: PRIMARY + '1A',
    borderWidth: 1,
    borderColor: PRIMARY + '33',
  },
  v2BadgeText: { fontFamily: theme.fonts.bold, fontSize: 11, color: PRIMARY },
  courseName: { fontFamily: theme.fonts.bold, fontSize: 16, color: TEXT, textAlign: 'right', lineHeight: 22 },
  courseDesc: { fontFamily: theme.fonts.regular, fontSize: 13, color: TEXT_MED, textAlign: 'right', lineHeight: 19 },

  statsRow: { flexDirection: 'row-reverse', gap: 6, flexWrap: 'wrap' },
  statPill: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 4,
    backgroundColor: SURFACE2,
    borderWidth: 1,
    borderColor: SURFACE3,
    paddingHorizontal: 9,
    paddingVertical: 3,
    borderRadius: 20,
  },
  statPillIcon: { fontSize: 11 },
  statPillText: { fontFamily: theme.fonts.medium, fontSize: 12, color: TEXT_MED },

  nextActionRow: { marginTop: 10, borderWidth: 1, borderColor: PRIMARY + '35', borderRadius: 10, paddingVertical: 8, paddingHorizontal: 10, backgroundColor: PRIMARY + '10' },
  nextActionText: { color: PRIMARY, fontFamily: theme.fonts.medium, fontSize: 12, textAlign: 'right' },
  cardActions: { flexDirection: 'row-reverse', gap: 6, flexWrap: 'wrap', marginTop: 2 },
  actionBtn: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: SURFACE3,
    backgroundColor: SURFACE2,
  },
  actionBtnCopied: { borderColor: GREEN + '44', backgroundColor: GREEN + '0F' },
  actionBtnText: { fontFamily: theme.fonts.medium, fontSize: 12, color: TEXT_MED },
  publishBtn: {
    paddingHorizontal: 14,
    paddingVertical: 7,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: GREEN + '44',
    backgroundColor: GREEN + '10',
  },
  publishBtnText: { fontFamily: theme.fonts.bold, fontSize: 12, color: GREEN },

  createMoreBtn: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    borderWidth: 1,
    borderStyle: 'dashed' as any,
    borderColor: PRIMARY + '44',
    borderRadius: 14,
    paddingVertical: 14,
    backgroundColor: PRIMARY + '06',
  },
  createMoreText: { fontFamily: theme.fonts.semiBold, fontSize: 14, color: PRIMARY },

  emptyState: { alignItems: 'center', paddingVertical: 24, gap: 12 },
  emptyIconWrap: {
    width: 80,
    height: 80,
    borderRadius: 22,
    backgroundColor: PRIMARY + '14',
    borderWidth: 1,
    borderColor: PRIMARY + '28',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 4,
  },
  emptyIcon: { fontSize: 38 },
  emptyTitle: { fontFamily: theme.fonts.bold, fontSize: 20, color: TEXT, textAlign: 'center' },
  emptySub: { fontFamily: theme.fonts.regular, fontSize: 14, color: TEXT_MED, textAlign: 'center', lineHeight: 20 },
  emptyWaysGrid: { flexDirection: 'row-reverse', flexWrap: 'wrap', gap: 10, justifyContent: 'center', marginTop: 4 },
  emptyWayCard: {
    backgroundColor: SURFACE,
    borderRadius: 14,
    padding: 16,
    width: 138,
    alignItems: 'center',
    gap: 6,
    borderWidth: 1,
    borderColor: SURFACE3,
    ...(Platform.OS === 'web' ? ({ boxShadow: '0 4px 16px rgba(0,0,0,0.3)' } as any) : {}),
  },
  emptyWayIcon: { fontSize: 26 },
  emptyWayLabel: { fontFamily: theme.fonts.semiBold, fontSize: 13, color: TEXT, textAlign: 'center' },
  emptyWaySub: { fontFamily: theme.fonts.regular, fontSize: 11, color: TEXT_MED, textAlign: 'center' },
  emptyBtn: {
    backgroundColor: PRIMARY,
    paddingHorizontal: 28,
    paddingVertical: 14,
    borderRadius: 14,
    marginTop: 4,
    ...(Platform.OS === 'web' ? ({ boxShadow: `0 6px 20px ${PRIMARY}55` } as any) : {}),
  },
  emptyBtnText: { fontFamily: theme.fonts.bold, fontSize: 15, color: '#fff' },
});
